import { Route, Router, Switch } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import MainPage from './pages/mainPage/MainPage';
import ApproachPage from './pages/approachPage/ApproachPage';
import ProgramPage from './pages/programPage/ProgramPage';
import PricingPage from './pages/pricingPage/PricingPage';
import NurseryPage from './pages/nurseryPage/NurseryPage';
import EnrollPage from './pages/enrollPage/EnrollPage';
import ReviewsV2Page from './pages/reviewsPage/ReviewsV2Page';
// import BlogPage from './pages/blogPage/BlogPage';
// import BlogDetailPage from './pages/blogPage/BlogDetailPage';
import NotFoundPage from './elements/NotFoundPage';
import NavBar from './elements/NavBar';
import FooterFrame from './elements/FooterFrame';
import SEO from './elements/SEO';
import theme from './elements/Theme';
import ChristmasPromoModal from './elements/ChristmasPromoModal';
import {
  PROMO_POPUP_DELAY,
  PROMO_RESHOW_DELAY,
  isPromoActive,
  hasUserPermanentlyDismissedPromo,
  getDismissCount,
  markPromoAsDismissed,
  incrementAppearanceCount,
  getAppearanceCount,
} from './constants/promoConfig';
import './assets/fonts.css';
import 'normalize.css';
import './i18n';
import "./globals.css";

const history = createBrowserHistory();

function App() {
  const { i18n } = useTranslation();
  const [showChristmasPromo, setShowChristmasPromo] = useState(false);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  // Track popup appearances
  useEffect(() => {
    if (showChristmasPromo) {
      incrementAppearanceCount();
      console.log(`Popup appearance count: ${getAppearanceCount()}`);
    }
  }, [showChristmasPromo]);

  // Christmas promo popup logic
  useEffect(() => {
    // Only proceed if promo is still active and not permanently dismissed
    if (!isPromoActive() || hasUserPermanentlyDismissedPromo()) {
      return;
    }

    const dismissCount = getDismissCount();

    if (dismissCount === 0) {
      // Never dismissed: show after initial delay
      const initialTimer = setTimeout(() => {
        setShowChristmasPromo(true);
      }, PROMO_POPUP_DELAY);

      return () => clearTimeout(initialTimer);
    } else if (dismissCount >= 1 && !hasUserPermanentlyDismissedPromo()) {
      // Dismissed before (but not permanently): show immediately and start repeating
      setShowChristmasPromo(true);

      const repeatInterval = setInterval(() => {
        if (isPromoActive() && !hasUserPermanentlyDismissedPromo()) {
          setShowChristmasPromo(true);
        } else {
          clearInterval(repeatInterval);
        }
      }, PROMO_RESHOW_DELAY);

      setIntervalId(repeatInterval);
      return () => clearInterval(repeatInterval);
    }
  }, []); // Empty dependency array - only run once on mount

  const handleCloseChristmasPromo = () => {
    setShowChristmasPromo(false);
    const currentDismissCount = getDismissCount();

    // Check if we've reached the max appearances
    if (hasUserPermanentlyDismissedPromo()) {
      if (intervalId) {
        clearInterval(intervalId);
        setIntervalId(null);
      }
      return;
    }

    markPromoAsDismissed();

    // If this was the first dismissal, start the repeat cycle
    if (currentDismissCount === 0) {
      // Start showing popup every PROMO_RESHOW_DELAY
      const repeatInterval = setInterval(() => {
        if (isPromoActive() && !hasUserPermanentlyDismissedPromo()) {
          setShowChristmasPromo(true);
        } else {
          clearInterval(repeatInterval);
        }
      }, PROMO_RESHOW_DELAY);

      setIntervalId(repeatInterval);
    }
  };

  const renderWithLanguage = (Component: any, lng: any) => (props: any) => {
    if (i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
    return <Component {...props} />;
  };

  return (
    <ThemeProvider theme={theme}>
      <Router history={history}>
        <SEO>
          <ChristmasPromoModal
            open={showChristmasPromo}
            onClose={handleCloseChristmasPromo}
          />
          <NavBar />
          <Switch>
            <Route exact path="/" render={renderWithLanguage(MainPage, 'en')} />
            <Route exact path="/landing" render={renderWithLanguage(MainPage, 'en')} />
            <Route path="/approach" render={renderWithLanguage(ApproachPage, 'en')} />
            <Route path="/our-program" render={renderWithLanguage(ProgramPage, 'en')} />
            <Route path="/pricing" render={renderWithLanguage(PricingPage, 'en')} />
            <Route path="/nursery" render={renderWithLanguage(NurseryPage, 'en')} />
            <Route path="/enroll" render={renderWithLanguage(EnrollPage, 'en')} />
            <Route path="/reviews" render={renderWithLanguage(ReviewsV2Page, 'en')} />
            {/* <Route exact path="/blog" render={renderWithLanguage(BlogPage, 'en')} />
            <Route exact path="/blog/:slug" render={renderWithLanguage(BlogDetailPage, 'en')} /> */}

            <Route exact path="/ru" render={renderWithLanguage(MainPage, 'ru')} />
            <Route exact path="/ru/landing" render={renderWithLanguage(MainPage, 'ru')} />
            <Route path="/ru/approach" render={renderWithLanguage(ApproachPage, 'ru')} />
            <Route path="/ru/our-program" render={renderWithLanguage(ProgramPage, 'ru')} />
            <Route path="/ru/pricing" render={renderWithLanguage(PricingPage, 'ru')} />
            <Route path="/ru/nursery" render={renderWithLanguage(NurseryPage, 'ru')} />
            <Route path="/ru/enroll" render={renderWithLanguage(EnrollPage, 'ru')} />
            <Route path="/ru/reviews" render={renderWithLanguage(ReviewsV2Page, 'ru')} />
            {/* <Route exact path="/ru/blog" render={renderWithLanguage(BlogPage, 'ru')} />
            <Route exact path="/ru/blog/:slug" render={renderWithLanguage(BlogDetailPage, 'ru')} /> */}

            <Route exact path="/sr" render={renderWithLanguage(MainPage, 'sr')} />
            <Route exact path="/sr/landing" render={renderWithLanguage(MainPage, 'sr')} />
            <Route path="/sr/approach" render={renderWithLanguage(ApproachPage, 'sr')} />
            <Route path="/sr/our-program" render={renderWithLanguage(ProgramPage, 'sr')} />
            <Route path="/sr/pricing" render={renderWithLanguage(PricingPage, 'sr')} />
            <Route path="/sr/nursery" render={renderWithLanguage(NurseryPage, 'sr')} />
            <Route path="/sr/enroll" render={renderWithLanguage(EnrollPage, 'sr')} />
            <Route path="/sr/reviews" render={renderWithLanguage(ReviewsV2Page, 'sr')} />
            {/* <Route exact path="/sr/blog" render={renderWithLanguage(BlogPage, 'sr')} />
            <Route exact path="/sr/blog/:slug" render={renderWithLanguage(BlogDetailPage, 'sr')} /> */}
            
            <Route path="*" component={NotFoundPage} />
          </Switch>
          <FooterFrame />
        </SEO>
      </Router>
    </ThemeProvider>
  );
}

export default App;
